import { IHttpService, ICompileService, IScope } from "angular";
import { RecordDataSource } from "../../../../../../records/grids";
import {
  IGridSettings,
  NullGridSettings,
} from "../../../../../../records/grids/settings/gridSettingsService";
import { IQuery } from "../../../../../../records/server/query";
import { FilterProvider } from "../../../../../../records/grids/columns/filters/FilterProvider";
import { LookupFilter } from "../../../../../../records/grids/columns/filters/subFilters/LookupFilter";
import { OptionsFilter } from "../../../../../../records/grids/columns/filters/subFilters/OptionsFilter";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../../../../../records/server/serverUtility";
import {
  booleanValueGetterFactory,
  dateValueGetter,
  lookupValueGetterFactory,
} from "../../../../../../records/grids/cells/valueGetters";
import { Record } from "../../../../../../records/server/crud";
import { uniq } from "lodash";
import { ColDef } from "../../../../../../records/grids/columns/colDef";
import { machineConfigurationCellClass } from "../../../../../grids/cellHelpers";

const apiUrl = "/api/machines/progressives/MachineConfigurations";
const progressiveDefinitionApiUrl =
  "/api/machines/progressives/ProgressiveDefinitionMachine";

export class ProgressiveDefinitionsDetailMachineEditViewController {
  static $inject = [
    "$compile",
    "progressiveDefinition",
    "currentProgressiveDefinitionMachines",
    ServerUtilityName,
    "date",
  ];

  readonly columns: ColDef[];
  readonly gridSettings: IGridSettings;
  readonly defaultQuery: IQuery;
  readonly gridDatasource: RecordDataSource;
  readonly initialMachineConfigurationIds: number[];

  constructor(
    $compile: ICompileService,
    private readonly progressiveDefinition: Record,
    private readonly currentProgressiveDefinitionMachines: Record[],
    private readonly serverUtility: ServerUtility,
    date: Date
  ) {
    this.defaultQuery = { PropertyId: progressiveDefinition.PropertyId };

    this.initialMachineConfigurationIds = uniq(
      currentProgressiveDefinitionMachines.map(
        (pdm) => <number>pdm.MachineConfigurationId
      )
    );
    this.columns = [
      {
        colId: "select",
        headerName: "Select",
        template: `
          <input
            type="checkbox"
            ng-click="recordGridController.toggleRowSelection(data)"
            ng-checked="recordGridController.rowSelected(data)"
          />`,
        suppressSorting: true,
      },
      {
        field: "Id",
        headerName: "Id",
        filter: new FilterProvider("Id", $compile)
          .defaultNumberFilters()
          .getGridFilterFactory(),
        hide: true,
      },
      {
        field: "Date",
        headerName: "Date",
        filter: new FilterProvider("Date", $compile)
          .defaultDateFilters()
          .getGridFilterFactory(),
        valueGetter: dateValueGetter,
      },
      {
        field: "ManufacturerName",
        headerName: "Manufacturer",
        filter: new FilterProvider("ManufacturerName", $compile)
          .defaultStringFilters()
          .addFilter(
            "=",
            new LookupFilter({
              displayName: "=",
              queryFieldNames: ["ManufacturerId"],
              serverQuery: (search: string) => {
                return serverUtility.lookupQuery({
                  url: "/api/machines/manufacturers",
                  search,
                });
              },
            })
          )
          .getGridFilterFactory(),
      },
      {
        field: "MachineSerialNumber",
        headerName: "Serial Number",
        filter: new FilterProvider("MachineSerialNumber", $compile)
          .defaultStringFilters()
          .getGridFilterFactory(),
      },
      {
        field: "SlotNumber",
        headerName: "Slot Number",
        filter: new FilterProvider("SlotNumber", $compile)
          .defaultStringFilters()
          .getGridFilterFactory(),
      },
      {
        field: "Section",
        headerName: "Section",
        filter: new FilterProvider("Section", $compile)
          .defaultStringFilters()
          .getGridFilterFactory(),
        hide: true,
      },
      {
        field: "Bank",
        headerName: "Bank",
        filter: new FilterProvider("Bank", $compile)
          .defaultStringFilters()
          .getGridFilterFactory(),
        hide: true,
      },
      {
        field: "Slot",
        headerName: "Slot",
        filter: new FilterProvider("Slot", $compile)
          .defaultStringFilters()
          .getGridFilterFactory(),
        hide: true,
      },
      {
        field: "Location",
        headerName: "Location",
        filter: new FilterProvider("Location", $compile)
          .defaultStringFilters()
          .getGridFilterFactory(),
      },
      {
        field: "GameName",
        headerName: "Game Name",
        filter: new FilterProvider("GameName", $compile)
          .defaultStringFilters()
          .getGridFilterFactory(),
      },
      {
        field: "Status",
        headerName: "Status",
        filter: new FilterProvider("Status", $compile)
          .addFilter(
            "=",
            new OptionsFilter({
              displayName: "=",
              options: [
                { display: "Floor", value: "Floor" },
                { display: "Decommissioned", value: "Decommissioned" },
                { display: "Warehoused", value: "Warehoused" },
              ],
              queryFieldNames: ["Status"],
              includeNullValue: true,
            })
          )
          .getGridFilterFactory(),
      },
      {
        field: "ChangeType",
        headerName: "Change Type",
        valueGetter: lookupValueGetterFactory({
          lookup: {
            NewMachine: "New Machine",
          },
        }),
        filter: new FilterProvider("ChangeType", $compile)
          .addFilter(
            "=",
            new OptionsFilter({
              displayName: "=",
              options: [
                { display: "Move", value: "Move" },
                { display: "Conversion", value: "Conversion" },
                { display: "New Machine", value: "NewMachine" },
                { display: "Unknown", value: "Unknown" },
                { display: "Decommission", value: "Decommission" },
                { display: "Warehouse", value: "Warehouse" },
              ],
              queryFieldNames: ["ChangeType"],
              includeNullValue: true,
            })
          )
          .getGridFilterFactory(),
      },
      {
        field: "CurrentConfiguration",
        headerName: "Current Configuration",
        filter: new FilterProvider("CurrentConfiguration", $compile)
          .defaultBooleanFilters()
          .getGridFilterFactory(),
        valueGetter: booleanValueGetterFactory(),
      },
    ];
    const cssClassFunction = machineConfigurationCellClass(
      date,
      "CurrentConfiguration",
      "Date"
    );
    this.columns.forEach((c) => {
      c.cellClass = cssClassFunction;
    });
    this.gridSettings = new NullGridSettings();
    this.gridDatasource = new RecordDataSource(serverUtility, apiUrl, true);
  }

  gridSelectionChanged(row: Record, selected: boolean) {
    const machineConfigurationId = <number>row.Id;
    if (selected) {
      this.addProgressiveDefinitionMachine(machineConfigurationId);
    } else {
      this.removeProgressiveDefinitionMachine(machineConfigurationId);
    }
  }

  private addProgressiveDefinitionMachine(machineConfigurationId: number) {
    this.serverUtility
      .postWithCrudRecordResponse<Record>({
        url: progressiveDefinitionApiUrl,
        postObject: {
          MachineConfigurationId: machineConfigurationId,
          ProgressiveDefinitionId: this.progressiveDefinition.Id,
          Active: true,
        },
      })
      .then((response) => {
        this.currentProgressiveDefinitionMachines.push(response.Result);
      });
  }

  private removeProgressiveDefinitionMachine(machineConfigurationId: number) {
    const progressiveDefinitionMachine =
      this.currentProgressiveDefinitionMachines.find(
        (pdm) => pdm.MachineConfigurationId === machineConfigurationId
      );
    if (!progressiveDefinitionMachine) {
      return;
    }
    this.serverUtility
      .delete<Record>({
        url: progressiveDefinitionApiUrl,
        id: progressiveDefinitionMachine.Id,
      })
      .then(() => {
        const index = this.currentProgressiveDefinitionMachines.indexOf(
          progressiveDefinitionMachine
        );
        if (index > -1) {
          this.currentProgressiveDefinitionMachines.splice(index, 1);
        }
      });
  }

  currentConfigurationsOnlyChanged(activeConfigurationsOnly: boolean) {
    this.defaultQuery.CurrentConfiguration = activeConfigurationsOnly
      ? true
      : undefined;
  }

  progressiveConfigurationsOnlyChanged(progressiveConfigurationsOnly: boolean) {
    this.defaultQuery.Ids = progressiveConfigurationsOnly
      ? this.currentProgressiveDefinitionMachines.map(
          (pdm) => <number>pdm.MachineConfigurationId
        )
      : undefined;
  }
}
